import { useState, type FormEvent } from 'react';
import { Field, Input, Select } from '@caios/ui';

/**
 * Hailer's lead loader (11-05): a client's or a brand's CSV straight onto a call list, ranked in the file's order. Columns
 * are matched by name (with the usual synonyms); rows go in 400 at a time through the Leads import, which matches a
 * business already on the list instead of duplicating it. Problems come back by CSV line.
 */
async function send<T>(path: string, body: unknown): Promise<T> {
  const r = await fetch(path, { method: 'POST', credentials: 'include', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error ?? `Request failed (${r.status})`);
  return j as T;
}

const SYN: Record<string, string[]> = {
  businessName: ['business name', 'business', 'company name', 'company', 'account', 'name', 'organization', 'organisation'],
  website: ['website', 'web site', 'url', 'site', 'domain', 'website url'],
  phone: ['phone', 'main phone', 'phone number', 'telephone', 'tel', 'main line', 'office phone', 'business phone'],
  email: ['email', 'e-mail', 'email address', 'owner email', 'contact email'],
  contactName: ['owner', 'owner name', 'contact', 'contact name', 'full name', 'decision maker'],
  firstName: ['first name', 'first', 'firstname'], lastName: ['last name', 'last', 'lastname', 'surname'],
  contactTitle: ['title', 'job title', 'role', 'position'],
  address: ['address', 'street', 'street address', 'address 1'], city: ['city', 'town'], region: ['state', 'region', 'province', 'st'],
  industry: ['industry', 'category', 'vertical', 'niche', 'type'],
};
const LABEL: Record<string, string> = { businessName: 'Business', website: 'Website', phone: 'Phone', email: 'Email', contactName: 'Contact', firstName: 'First name', lastName: 'Last name',
  contactTitle: 'Title', address: 'Address', city: 'City', region: 'State', industry: 'Industry' };
const norm = (h: string) => h.trim().toLowerCase().replace(/[_\-.]+/g, ' ').replace(/\s+/g, ' ');

/** RFC 4180 enough: quoted fields, doubled quotes, commas and newlines inside quotes. */
export function parseCsv(text: string): string[][] {
  const out: string[][] = []; let row: string[] = [], cell = '', q = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (q) { if (c === '"') { if (text[i + 1] === '"') { cell += '"'; i++; } else q = false; } else cell += c; continue; }
    if (c === '"') q = true; else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') { if (c === '\r' && text[i + 1] === '\n') i++; row.push(cell); out.push(row); row = []; cell = ''; }
    else cell += c;
  }
  if (cell || row.length) { row.push(cell); out.push(row); }
  return out.filter((r) => r.some((x) => x.trim()));
}

export function LoadCsv({ brands, onDone }: { brands: { account_id: string; name: string }[]; onDone: (listId: string | null, added: number, accountId: string) => void }) {
  const [accountId, setAccountId] = useState(brands[0]?.account_id ?? '');
  const [name, setName] = useState('');
  const [file, setFile] = useState<{ head: string[]; rows: string[][]; map: Record<number, string> } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [problems, setProblems] = useState<{ line: number; why: string }[]>([]);
  const read = async (f: File | undefined) => {
    setErr(null); setProblems([]);
    if (!f) return setFile(null);
    const all = parseCsv(await f.text());
    if (all.length < 2) return setErr('That file has no rows under its header.');
    const head = all[0]!, map: Record<number, string> = {}, used = new Set<string>();
    head.forEach((h, i) => { const n = norm(h); const k = Object.keys(SYN).find((key) => !used.has(key) && SYN[key]!.includes(n)); if (k) { map[i] = k; used.add(k); } });
    setFile({ head, rows: all.slice(1), map });
  };
  const rowOf = (r: string[]) => {
    const o: Record<string, string> = {};
    for (const [i, k] of Object.entries(file!.map)) { const v = r[Number(i)]?.trim(); if (v) o[k] = v; }
    if (!o.contactName && (o.firstName || o.lastName)) o.contactName = [o.firstName, o.lastName].filter(Boolean).join(' ');
    delete o.firstName; delete o.lastName;
    return o;
  };
  const go = async (e: FormEvent) => {
    e.preventDefault(); if (!file) return;
    setErr(null); setProblems([]);
    const batch = `csv-${new Date().toISOString().slice(0, 16)}`, ids: string[] = [], bad: { line: number; why: string }[] = [];
    let created = 0, merged = 0;
    try {
      for (let at = 0; at < file.rows.length; at += 400) {
        setBusy(`Loading ${Math.min(at + 400, file.rows.length).toLocaleString()} of ${file.rows.length.toLocaleString()}…`);
        const r = await send<{ created: number; merged: number; skipped: { row: number; why: string }[]; ids: string[] }>('/api/v1/sales/leads/import',
          { accountId, importBatch: batch, source: 'csv', rows: file.rows.slice(at, at + 400).map(rowOf) });
        created += r.created; merged += r.merged; ids.push(...r.ids);
        bad.push(...r.skipped.map((s) => ({ line: at + s.row + 1, why: s.why })));   // +1: the header is line 1
      }
      setProblems(bad);
      if (name.trim()) {
        const keep = [...new Set(ids)].slice(0, 5000);
        const l = await send<{ id: string; added: number }>('/api/v1/sales/call-lists', { accountId, name: name.trim(), filters: { leadIds: keep, keepOrder: true } });
        onDone(l.id, l.added, accountId);
      } else onDone(null, created + merged, accountId);
    } catch (x) { setErr((x as Error).message); } finally { setBusy(null); }
  };
  const recognised = file ? Object.values(file.map) : [];
  const ignored = file ? file.head.filter((_, i) => !file.map[i]) : [];
  const preview = file?.rows.slice(0, 5).map(rowOf) ?? [];
  return <form className="cx-stack" style={{ gap: 12 }} onSubmit={go} data-testid="hl-csv">
    <div className="cx-fields">
      <Field label="Leads for"><Select value={accountId} onChange={(e) => setAccountId(e.target.value)}>{brands.map((b) => <option key={b.account_id} value={b.account_id}>{b.name}</option>)}</Select></Field>
      <Field label="Call list" hint="Optional: built in the file’s order, as a draft"><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Roofing — Tampa Bay" data-testid="hl-csv-name" /></Field>
    </div>
    <Field label="CSV file"><input type="file" accept=".csv,text/csv" onChange={(e) => void read(e.target.files?.[0])} data-testid="hl-csv-file" /></Field>
    {file && <>
      <div className="cx-faint" style={{ fontSize: 12.5 }}><b style={{ color: 'var(--cx-text)' }}>{file.rows.length.toLocaleString()} rows.</b> Using {recognised.map((k) => LABEL[k]).join(', ') || 'nothing'}
        {ignored.length ? <> · ignoring {ignored.join(', ')}</> : null}</div>
      {!recognised.includes('businessName') && !recognised.includes('website') && <div className="cx-note cx-note--warn">No business name or website column found: every row would be skipped.</div>}
      <div style={{ overflowX: 'auto' }}><table className="hl-hist"><thead><tr>{recognised.filter((k) => k !== 'firstName' && k !== 'lastName').map((k) => <th key={k}>{LABEL[k]}</th>)}{recognised.some((k) => k === 'firstName' || k === 'lastName') && <th>Contact</th>}</tr></thead>
        <tbody>{preview.map((r, i) => <tr key={i}>{recognised.filter((k) => k !== 'firstName' && k !== 'lastName').map((k) => <td key={k}>{r[k] ?? ''}</td>)}{recognised.some((k) => k === 'firstName' || k === 'lastName') && <td>{r.contactName ?? ''}</td>}</tr>)}</tbody></table></div>
    </>}
    {problems.length > 0 && <div className="cx-note cx-note--warn"><b>{problems.length} row{problems.length === 1 ? '' : 's'} skipped:</b> {problems.slice(0, 8).map((p) => `line ${p.line} (${p.why})`).join('; ')}{problems.length > 8 ? '…' : ''}</div>}
    {err && <div className="cx-note cx-note--fire" role="alert">{err}</div>}
    <div><button type="submit" className="cx-btn cx-btn--primary" disabled={!file || !!busy || !accountId} data-testid="hl-csv-go">{busy ?? (name.trim() ? 'Load and build the list' : 'Load the leads')}</button></div>
    <p className="cx-faint" style={{ margin: 0, fontSize: 12.5 }}>A business already on this brand’s list is matched (website, then phone) and gets any new numbers or emails; nothing is overwritten.</p>
  </form>;
}
